import React from 'react';
import ReactDOM from 'react-dom';
import {createRoot} from 'react-dom/client';
import appReducer from 'common/reducers/app-reducer';
import AppRouter from 'common/routers/app-router';
import appStore from 'common/stores/app-store';
import {htmlUnescape} from 'common/utils/string-util';

export const FILL_VIEWPORT_CSS =
  'position: fixed;top: 0;left: 0;bottom: 0;right: 0;';

export const appendPx = (input) => {
  if (typeof input === 'number' && input !== Infinity && !isNaN(input)) {
    return `${input}px`;
  }
  return '0px';
};

export const getBooleanFromString = (input) => {
  if (input === 'true') {
    return true;
  }
  return false;
};

export const getClass = (input) => {
  return `.${input}`;
};

export const getClipPath = (input) => {
  return `url(#${input})`;
};

export const getID = (input) => {
  return `#${input}`;
};

export const getTranslate = (x, y) => {
  return `translate(${x},${y})`;
};

export const getTranslateCoords = (input) => {
  const regExp = /\(([^)]+)\)/;
  const matches = regExp.exec(input);
  const coords = matches[1].split(',');
  coords[0] = parseFloat(coords[0]);
  coords[1] = parseFloat(coords[1]);
  return coords;
};

export const removePx = (input) => {
  if (typeof input === 'string') {
    const split = input.split('px');
    return parseInt(split[0], 10) || 0;
  }
  return 0;
};

export const removeTdStyles = (row) => {
  const childList = Array.prototype.slice.call(row.childNodes);
  childList.forEach(
    (td) => td.hasAttribute('style') && td.removeAttribute('style'),
  );
};

/* Gets cumulative offset from the top of the document for a given element */
export const getDocumentOffsetTop = (targetElement) => {
  let offset = 0;
  let element = targetElement;
  while (element) {
    offset += element.offsetTop;
    element = element.offsetParent;
  }
  return offset;
};

// Given a node and an element id, find the closest ancestor
// node that has that id
export const getClosestAncestorWithId = (node, id) => {
  let parentEl = node.parentElement;
  while (parentEl) {
    if (parentEl.id !== id) {
      parentEl = parentEl.parentElement;
    } else {
      return parentEl;
    }
  }
  return null;
};

// Given a node and a css class, find the closest ancestor node that has
// that css class
export const getClosestAncestorWithClass = (node, clazz) => {
  let parentEl = node.parentElement;
  while (parentEl) {
    const parentClassList = Array.prototype.slice.call(parentEl.classList);
    if (parentClassList.indexOf(clazz) === -1) {
      parentEl = parentEl.parentElement;
    } else {
      return parentEl;
    }
  }
  return null;
};

// Given a node, find the closest ancestor node that does not
// have a position of static
export const getClosestPositionedAncestor = (node) => {
  let parentEl = node.parentElement;

  while (parentEl) {
    const parentPosition = getComputedStyle(parentEl).position;
    if (parentPosition === 'static') {
      parentEl = parentEl.parentElement;
    } else {
      return parentEl;
    }
  }

  return window;
};

// Given a table row, return a list of the offsetWidths of
// the child td nodes
export const getTdWidths = (row) => {
  const childLst = Array.prototype.slice.call(row.children);
  return childLst.map((td) => td.offsetWidth);
};

// Given a table row and an array of numbers, assign the numbers
// as pixel widths to the child td nodes of the row
export const assignTdsWidths = (row, pixelValues) => {
  const childLst = Array.prototype.slice.call(row.children);
  childLst.forEach((td, index) => {
    td.style.width = `${pixelValues[index]}px`;
  });
};

// Given a table row and an array of numbers, assign the
// width values to the child td nodes of the row
export const assignTdsStyleValues = (row, property, values) => {
  const childLst = Array.prototype.slice.call(row.children);
  childLst.forEach((td, index) => {
    td.style[property] = values[index];
  });
};

// Copies style proportional arguments from a source element to a destination
// one. Use a thead > tr as destination, and tbody > tr as source.
export const copyTableProportions = (source, destination) => {
  const length = source.children.length;

  // Copy over widths of each column (assumed to be children)
  for (let index = 0; index < length; index++) {
    const [sourceChild, destinationChild] = [
      source.children[index],
      destination.children[index],
    ];
    destinationChild.style.width = `${sourceChild.offsetWidth}px`;
  }

  // Copy over total width
  destination.style.width = source.offsetWidth;

  // Copy over total height
  destination.style.height = source.offsetHeight;
};

export const getScroll = (element = null, method = 'Top') => {
  // The passed in `method` value should be 'Top' or 'Left'
  if (element === null || element === window || element === document) {
    return window[method === 'Top' ? 'pageYOffset' : 'pageXOffset'];
  }
  return element[`scroll${method}`];
};

// Empirically determines the effective viewport size with a test element.
// This is because window.innerWidth seems to give slightly off answers.
export const testViewportSize = () => {
  // Create and insert a fixed test element which fills the viewport
  const testElement = document.createElement('div');
  testElement.style.cssText = FILL_VIEWPORT_CSS;
  document.documentElement.insertBefore(
    testElement,
    document.documentElement.firstChild,
  );

  // Capture dimensions of element and remove from document
  const dimensions = {
    width: testElement.offsetWidth,
    height: testElement.offsetHeight,
  };
  document.documentElement.removeChild(testElement);

  return dimensions;
};

// Given a node an id or class selector, find the closest ancestor
// node that has that id or class
export const getClosestAncestorWithIdOrClass = (node, selector) => {
  const selectorTypeIndicator = selector[0];
  const selectorName = selector.slice(1);
  let ancestor;

  if (selectorTypeIndicator === '.') {
    ancestor = getClosestAncestorWithClass(node, selectorName);
  } else if (selectorTypeIndicator === '#') {
    ancestor = getClosestAncestorWithId(node, selectorName);
  } else {
    throw new Error('Invalid selector - must be a css class or element id');
  }

  return ancestor;
};

export const injectReactElement = (targetNode, element) => {
  // reuse old root if we're reinserting to same node
  if (!targetNode.reactRoot) {
    targetNode.reactRoot = createRoot(targetNode);
  }
  targetNode.reactRoot.render(element);
};

export const unmountReactElement = (targetNode) => {
  targetNode?.reactRoot?.unmount();
  if (targetNode.reactRoot) {
    targetNode.reactRoot = null;
  }
};

export const adapt = (component, selector, parseProps) => {
  const mounts = document.querySelectorAll(selector);
  Array.from(mounts).forEach((mount) => {
    const props = parseProps ? parseProps(mount) : {};
    const element = React.createElement(component, props);
    injectReactElement(mount, element);
  });
};

export const unescapeObj = (object) => {
  const newObject = {};
  Object.keys(object).forEach(
    (key) => (newObject[key] = htmlUnescape(object[key])),
  );
  return newObject;
};

export const reactDOMInject = (component, selector, propsTransform = null) => {
  const targetNodes = document.querySelectorAll(selector);
  Array.from(targetNodes).forEach((targetNode) => {
    const dataset = unescapeObj(targetNode.dataset);
    const props = propsTransform ? propsTransform(dataset) : dataset;
    const element = React.createElement(component, props);
    injectReactElement(targetNode, element);
  });
};

export const appInject = (params) => {
  const selector = params.selector || '#app';
  reactDOMInject(AppRouter, selector, () => ({
    ...params,
    store: appStore(appReducer, {
      enums: window.enums,
      report: {},
      settings: window.settings,
    }),
  }));
};

// Returns an instance of passed class constructor, using
// props from DOM node dataset
export const classDOMInject = (Cls, selector, propsTransform = null) => {
  const targetNode = document.querySelector(selector);
  if (!targetNode) {
    return;
  }
  const dataset = unescapeObj(targetNode.dataset);
  const props = propsTransform ? propsTransform(dataset) : dataset;
  return new Cls(props);
};
